/* eslint-disable react/jsx-props-no-spreading */
import { PropTypes as MicromagPropTypes } from '@micromag/core';
import { Navbar, Button } from '@micromag/core/components';
import { useUrlGenerator } from '@micromag/core/contexts';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { useOrganisation } from '../../contexts/OrganisationContext';
import PreviewButton from '../buttons/Preview';
import PublishButton from '../buttons/Publish';
import UndoButton from '../buttons/Undo';
import LogoIcon from '../icons/Logo';

import styles from '../../../styles/navbars/navbar.module.scss';

const propTypes = {
    story: MicromagPropTypes.story,
    screenPath: PropTypes.string,
    saving: PropTypes.bool,
    canSave: PropTypes.bool,
    onUndo: PropTypes.func,
    canUndo: PropTypes.bool,
    className: PropTypes.string,
    onClickSave: PropTypes.func,
};

const defaultProps = {
    story: null,
    screenPath: null,
    saving: false,
    canSave: false,
    onUndo: null,
    canUndo: false,
    className: null,
    onClickSave: null,
};

const EditorNavbar = ({
    story,
    screenPath,
    saving,
    canSave,
    onClickSave,
    onUndo,
    canUndo,
    className,
}) => {
    const url = useUrlGenerator();
    const organisation = useOrganisation();
    const { permissions: organisationPermissions = null } = organisation || {};
    const { can_publish_story: organisationCanPublishStory = false } =
        organisationPermissions || {};
    const canPublishStory = organisation === null || organisationCanPublishStory;

    const link =
        story !== null && story.id
            ? url('stories.show', {
                  story: story.id,
              })
            : url('home');

    const pathPreview =
        story !== null && story.id && screenPath !== null
            ? url('stories.preview.path', {
                  story: story.id,
                  path: screenPath,
              })
            : null;
    const preview =
        story !== null && story.id
            ? url('stories.preview', {
                  story: story.id,
              })
            : null;

    const saveMessage = <FormattedMessage defaultMessage="Save" description="Save button label" />;
    const savedMessage = (
        <FormattedMessage defaultMessage="Saved" description="Saved button label" />
    );
    const finalSaveMessage = canSave ? saveMessage : savedMessage;
    const savingMessage = (
        <FormattedMessage defaultMessage="Saving..." description="Saving button label" />
    );

    return (
        <Navbar
            theme="dark"
            brand={<LogoIcon className={classNames([styles.logo, 'color-primary'])} />}
            brandLink={link}
            brandClassName={classNames([styles.brand, 'd-none', 'd-lg-inline-block'])}
            breadCrumbsClassName={classNames([styles.breadcrumbs, 'text-truncate'])}
            withoutCollapse
            noWrap
            className={classNames([styles.container, { [className]: className !== null }])}
        >
            <span className="navbar-text">{story !== null ? story.title : null}</span>
            <form className="form-inline ms-auto">
                {onUndo !== null ? <UndoButton onClick={onUndo} disabled={!canUndo} /> : null}
                <PreviewButton
                    href={pathPreview || preview}
                    theme="light"
                    invert
                    disabled={story === null || saving || canSave}
                    className={classNames(['me-1', styles.button])}
                >
                    <FormattedMessage defaultMessage="Preview" description="Preview Button label" />
                </PreviewButton>
                {canPublishStory ? (
                    <PublishButton
                        href={
                            story !== null
                                ? url('stories.publish', {
                                      story: story.id,
                                  })
                                : url('home')
                        }
                        theme="light"
                        invert
                        disabled={story === null || saving || canSave}
                        className={classNames(['me-1', styles.button])}
                    >
                        <FormattedMessage
                            defaultMessage="Publish"
                            description="Publish button label"
                        />
                    </PublishButton>
                ) : null}
                <Button href={link} theme="light" className={classNames(['me-1', styles.button])}>
                    <FormattedMessage defaultMessage="Close" description="Close button label" />
                </Button>
                <Button
                    theme="light"
                    disabled={story === null || saving || !canSave}
                    onClick={onClickSave}
                    className={classNames([styles.button])}
                >
                    {saving ? savingMessage : finalSaveMessage}
                </Button>
            </form>
        </Navbar>
    );
};

EditorNavbar.propTypes = propTypes;
EditorNavbar.defaultProps = defaultProps;

export default EditorNavbar;
